<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { VCard } from 'vuetify/components/VCard';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import useLocale from './helpers/use-locale';
import MdiFaIcon from './MdiFaIcon.vue';

library.add([faPlus, faTrashCan]);

export type SelectionAction = {
  key: string;
  label?: string;
  icon?: string;
  destructive?: boolean;
  placement?: 'leading' | 'trailing';
  disabled?: boolean;
  slot?: boolean;
  onSelect?: () => void;
};

export type SelectionActionPresentation = 'button' | 'icon' | 'menu';

type Props = {
  selectedContentTotal: number;
  actions?: SelectionAction[];
  showSelected?: boolean;
  isDisabled?: boolean;
  selectedLabel?: string;
  card?: boolean;
  iconSize?: number;
  rounded?: 'sm' | 'default' | 'lg';
};
const props = withDefaults(defineProps<Props>(), {
  actions: () => [],
  showSelected: true,
  isDisabled: false,
  selectedLabel: undefined,
  card: true,
  iconSize: undefined,
  rounded: undefined,
});

const { t } = useLocale();
const { xs } = useDisplay();

const roundedValue = computed(() => (props.rounded === 'default' ? undefined : props.rounded));

const actionRounded = computed(() => (props.card ? undefined : roundedValue.value));

const FA_ONE_EM_SIZE = 16;
const iconRenderSize = computed(() => props.iconSize ?? (props.card ? 20 : 18));

const countText = computed(
  () => props.selectedLabel ?? t('components.selected_contents', { count: props.selectedContentTotal }),
);

const isTrailing = (action: SelectionAction) => action.destructive || action.placement === 'trailing';
const leadingActions = computed(() => props.actions.filter((action) => !isTrailing(action)));
const trailingActions = computed(() => props.actions.filter(isTrailing));

const isStacked = computed(() => !props.card);

const promotedAction = computed(() =>
  isStacked.value && trailingActions.value.length === 0 && leadingActions.value.length === 2
    ? leadingActions.value[1]
    : undefined,
);

const collapseLeading = computed(() => isStacked.value && leadingActions.value.length > (promotedAction.value ? 2 : 1));

const renderedLeading = computed(() => {
  if (!isStacked.value) {
    return leadingActions.value;
  }
  if (collapseLeading.value) {
    return [];
  }
  return promotedAction.value ? leadingActions.value.slice(0, 1) : leadingActions.value;
});

const renderedTrailing = computed(() =>
  promotedAction.value ? [...trailingActions.value, promotedAction.value] : trailingActions.value,
);

const inlinePresentation = computed<SelectionActionPresentation>(() => (props.card ? 'button' : 'icon'));

const iconSizeFor = (icon?: string) => (icon?.startsWith('mdi-') ? iconRenderSize.value : FA_ONE_EM_SIZE);
</script>

<template>
  <component
    :is="card ? VCard : 'div'"
    v-bind="card ? { variant: 'tonal', rounded: roundedValue ?? 'default' } : {}"
    class="selected-contents"
    :class="{ 'selected-contents--stacked': !card }"
  >
    <span
      v-if="showSelected && card"
      class="selected-contents__count text-none px-2"
      :class="{ 'text-disabled': isDisabled }"
    >
      <slot name="default">{{ countText }}</slot>
    </span>
    <div class="d-flex selected-contents__actions align-center" :class="xs ? 'flex-row' : ''">
      <v-menu v-if="collapseLeading" location="top start">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            variant="outlined"
            :rounded="actionRounded"
            :disabled="isDisabled"
            :aria-label="t('components.label_more_actions')"
            icon
          >
            <v-icon :size="iconRenderSize">
              <MdiFaIcon :size="FA_ONE_EM_SIZE" icon="fa-solid fa-ellipsis" />
            </v-icon>
          </v-btn>
        </template>
        <v-card class="d-flex flex-column align-start pa-1">
          <template v-for="action in leadingActions" :key="action.key">
            <slot v-if="action.slot" :name="`action-${action.key}`" :action="action" presentation="menu"></slot>
            <v-btn
              v-else
              variant="text"
              class="text-none"
              :disabled="isDisabled || action.disabled"
              @click="action.onSelect?.()"
            >
              <v-icon v-if="action.icon" :size="iconRenderSize" start>
                <MdiFaIcon :size="iconSizeFor(action.icon)" :icon="action.icon" />
              </v-icon>
              {{ action.label }}
            </v-btn>
          </template>
        </v-card>
      </v-menu>
      <template v-for="action in renderedLeading" :key="action.key">
        <slot
          v-if="action.slot"
          :name="`action-${action.key}`"
          :action="action"
          :presentation="inlinePresentation"
        ></slot>
        <v-btn
          v-else
          class="text-none selected-contents__action"
          :class="card ? 'px-4' : 'px-2'"
          :variant="card ? 'text' : 'outlined'"
          :rounded="actionRounded"
          :size="card && xs ? 'small' : 'default'"
          :icon="!card"
          :disabled="isDisabled || action.disabled"
          :aria-label="action.label"
          @click="action.onSelect?.()"
        >
          <v-icon v-if="action.icon" :size="iconRenderSize" :start="card">
            <MdiFaIcon :size="iconSizeFor(action.icon)" :icon="action.icon" />
          </v-icon>
          <template v-if="card">{{ action.label }}</template>
        </v-btn>
      </template>
      <slot name="prepend"></slot>
      <slot name="actions"></slot>
    </div>
    <span
      v-if="showSelected && !card"
      class="selected-contents__count text-none px-2"
      :class="{ 'text-disabled': isDisabled }"
    >
      <slot name="default">{{ countText }}</slot>
    </span>
    <div class="d-flex align-center selected-contents__trailing">
      <template v-for="action in renderedTrailing" :key="action.key">
        <slot
          v-if="action.slot"
          :name="`action-${action.key}`"
          :action="action"
          :presentation="inlinePresentation"
        ></slot>
        <v-btn
          v-else
          class="text-none selected-contents__action"
          :class="[card ? 'px-4' : 'px-2', { 'text-error font-weight-bold': action.destructive }]"
          :variant="card ? 'text' : 'outlined'"
          :rounded="actionRounded"
          :size="card && xs ? 'small' : 'default'"
          :icon="!card"
          :disabled="isDisabled || action.disabled"
          :aria-label="action.label"
          @click="action.onSelect?.()"
        >
          <v-icon
            v-if="action.icon"
            :size="iconRenderSize"
            :start="card"
            :color="action.destructive ? 'error' : undefined"
          >
            <MdiFaIcon :size="iconSizeFor(action.icon)" :icon="action.icon" />
          </v-icon>
          <template v-if="card">{{ action.label }}</template>
        </v-btn>
      </template>
      <slot name="append"></slot>
      <slot name="trailing"></slot>
    </div>
  </component>
</template>

<style scoped lang="scss">
.selected-contents {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 12px;
  padding-inline: 16px;
  min-height: 48px;
  flex-wrap: nowrap;
}

.selected-contents__actions {
  align-items: center;
}

.selected-contents--stacked {
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;

  .selected-contents__actions,
  .selected-contents__trailing {
    flex: 1 1 0;
  }

  .selected-contents__actions {
    justify-content: flex-start;
  }

  .selected-contents__trailing {
    justify-content: flex-end;
  }

  .selected-contents__count {
    flex: 0 0 auto;
    text-align: center;
  }
}
</style>
